export interface PortfolioFundLimits
{
    titles: number;
    importe: number;
}

const PlaceholderValues = new Set(["", "-", "—", "–", "n/a", "na", "no aplica"]);

/**
 * Convierte importes/participaciones en formato es-ES/de-DE (miles con punto,
 * decimales con coma, símbolo €) a número. Los placeholders como "-" valen 0.
 */
export const parseSpanishFormattedNumber = (raw: unknown): number =>
{
    if(typeof raw === "number")
    {
        return Number.isFinite(raw) ? raw : 0;
    }
    if(raw == null)
    {
        return 0;
    }

    let text = String(raw).trim().toLowerCase();
    if(PlaceholderValues.has(text))
    {
        return 0;
    }

    text = text
        .replace(/€/g, "")
        .replace(/\beur\b/g, "")
        .replace(/\u00a0/g, "")
        .replace(/\s/g, "");

    if(text === "" || PlaceholderValues.has(text))
    {
        return 0;
    }

    const lastComma = text.lastIndexOf(",");
    const lastDot = text.lastIndexOf(".");
    if(lastComma >= 0 && lastComma > lastDot)
    {
        text = text.replace(/\./g, "").replace(",", ".");
    }
    else if(lastDot >= 0 && lastDot > lastComma && lastComma >= 0)
    {
        text = text.replace(/,/g, "");
    }
    else if(lastComma >= 0)
    {
        text = text.replace(",", ".");
    }
    else if(lastDot >= 0)
    {
        const parts = text.split(".");
        const lastPart = parts[parts.length - 1];
        if(parts.length > 2 || (parts.length === 2 && lastPart.length === 3))
        {
            text = parts.join("");
        }
    }

    const parsed = parseFloat(text);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Límites del fondo seleccionado: primero reporting de servidor; si aún no hay
 * datos para ese ISIN y coincide con el origin de navegación, se parsea la
 * valoración actual (no el coste de compra).
 */
export const resolveRefundPositionLimits = (
    selectedIsin: string | undefined,
    limitsByIsin: Record<string, PortfolioFundLimits>,
    navigationOrigin?: {isin?: string; titles?: unknown; valuation?: unknown; value?: unknown; bought?: unknown}
): PortfolioFundLimits =>
{
    if(selectedIsin != null && limitsByIsin[selectedIsin] != null)
    {
        return limitsByIsin[selectedIsin];
    }

    const originIsin = typeof navigationOrigin?.isin === "string" ? navigationOrigin.isin : undefined;
    if(navigationOrigin == null || selectedIsin == null || originIsin == null || selectedIsin !== originIsin)
    {
        return {titles: 0, importe: 0};
    }

    return{
        titles: parseSpanishFormattedNumber(navigationOrigin.titles),
        importe: parseSpanishFormattedNumber(navigationOrigin.valuation ?? navigationOrigin.value),
    };
};

/**
 * Umbral interno (89,75 %) para pasar a Total por importe. El copy habla de 90 %
 * a propósito: los fondos tratan ~90 % como reembolso total.
 */
export const TotalSwitchThreshold = 0.8975;

/**
 * Hay máximo usable si la cartera ya está lista o si el origin/reporting
 * ya nos dio un valor > 0. Si la cartera está lista y el máximo es 0,
 * la posición es realmente 0: hay que validar contra eso.
 */
export const arePositionLimitsUsable = (amountType: string, maxAmount: number, maxTitles: number, portfolioReady: boolean): boolean =>
{
    if(portfolioReady)
    {
        return true;
    }
    if(amountType === "amount")
    {
        return maxAmount > 0;
    }
    if(amountType === "titles")
    {
        return maxTitles > 0;
    }
    return false;
};

export type RefundAmountType = "amount" | "titles" | "total";

/** Solo amount, titles o total cuentan como tipo elegido. "" / undefined no. */
export const isRefundAmountType = (value: unknown): value is RefundAmountType =>
{
    return value === "amount" || value === "titles" || value === "total";
};

export type AutoSwitchWarning = "none" | "near90" | "full";

export type AutoSwitchToTotalResult =
{
    shouldSwitch: boolean;
    warning: AutoSwitchWarning;
};

const NoAutoSwitch: AutoSwitchToTotalResult = {shouldSwitch: false, warning: "none"};

export const resolveAutoSwitchToTotal = (amountType: string, entered: number, maxAmount: number, maxTitles: number): AutoSwitchToTotalResult =>
{
    if(!Number.isFinite(entered) || entered <= 0)
    {
        return NoAutoSwitch;
    }

    if(amountType === "amount")
    {
        if(!(maxAmount > 0))
        {
            return NoAutoSwitch;
        }
        if(entered >= maxAmount)
        {
            return {shouldSwitch: true, warning: "full"};
        }
        if(entered >= maxAmount * TotalSwitchThreshold)
        {
            return {shouldSwitch: true, warning: "near90"};
        }
        return NoAutoSwitch;
    }

    if(amountType === "titles")
    {
        // Por participaciones no se pasa a Total: el copy indica usar
        // participaciones exactas precisamente para evitar esa conversión.
        return NoAutoSwitch;
    }

    return NoAutoSwitch;
};

export const isRefundAmountWithinMax = (enteredRaw: string | undefined, maxValue: number, limitsUsable: boolean): boolean =>
{
    if(!limitsUsable)
    {
        return true;
    }
    const entered = parseFloat(enteredRaw ?? "");
    if(!Number.isFinite(entered))
    {
        return false;
    }
    return entered <= maxValue;
};

export const FundProductType = 21;

export type RefundReportingPosition =
{
    productType?: number;
    isin?: string | null;
    productName?: string;
    productShortName?: string;
    instrumentId?: string;
    titles?: unknown;
    value?: unknown;
};

export type RefundPortfolioFund =
{
    isin: string;
    name: string;
    finametrixId?: string;
};

export const isRefundablePortfolioPosition = (position: RefundReportingPosition): boolean =>
{
    return position.productType === FundProductType
        && typeof position.isin === "string"
        && position.isin !== "";
};

export const mapRefundablePortfolioFunds = (positions: unknown): RefundPortfolioFund[] =>
{
    if(!Array.isArray(positions))
    {
        return [];
    }

    return positions.filter(isRefundablePortfolioPosition).map((position: RefundReportingPosition) =>
    ({
        isin: position.isin as string,
        name: position.productName ?? position.productShortName ?? "",
        finametrixId: position.instrumentId
    }));
};

export const buildPortfolioLimitsByIsin = (positions: unknown): Record<string, PortfolioFundLimits> =>
{
    const limitsByIsin: Record<string, PortfolioFundLimits> = {};
    if(!Array.isArray(positions))
    {
        return limitsByIsin;
    }

    for(const position of positions)
    {
        if(!isRefundablePortfolioPosition(position) || position.isin == null)
        {
            continue;
        }
        limitsByIsin[position.isin] =
        {
            titles: parseSpanishFormattedNumber(position.titles),
            importe: parseSpanishFormattedNumber(position.value),
        };
    }

    return limitsByIsin;
};

export const isIsinInPortfolioFunds = (isin: unknown, portfolioFunds: RefundPortfolioFund[]): boolean =>
{
    if(typeof isin !== "string" || isin === "")
    {
        return false;
    }

    return portfolioFunds.some((fund) => fund.isin === isin);
};

export const filterRefundPortfolioFunds = (portfolioFunds: unknown, filter: unknown): any[] =>
{
    const list = Array.isArray(portfolioFunds) ? portfolioFunds : [];
    const filterStr = typeof filter === "string" ? filter.toLowerCase() : "";
    if(filterStr === "")
    {
        return list;
    }

    return list.filter((fund) =>
        (typeof fund?.isin === "string" && fund.isin.toLowerCase().includes(filterStr)) ||
        (typeof fund?.name === "string" && fund.name.toLowerCase().includes(filterStr))
    );
};

const readNumericField = (raw: unknown): number | null =>
{
    if(raw == null || raw === "")
    {
        return null;
    }
    const parsed = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Normaliza la respuesta de recargo de plan caducado (camelCase o PascalCase).
 * Si falta cualquier dato, el reembolso no puede continuar con recargo fiable.
 */
export const parseOutdatedRefundsRecharge = (response: any): {endDate: string; recharge: number; daysExpired: number} | null =>
{
    if(response == null || typeof response !== "object")
    {
        return null;
    }

    const endDate = response.endDate ?? response.EndDate;
    const recharge = readNumericField(response.recharge ?? response.Recharge);
    const daysExpired = readNumericField(response.daysExpired ?? response.DaysExpired);
    if(typeof endDate !== "string" || endDate.trim() === "" || recharge == null || daysExpired == null)
    {
        return null;
    }

    return{
        endDate,
        recharge,
        daysExpired,
    };
};

export const RefundIbanRequiredMessage = "Debes escoger una Cuenta Corriente para poder realizar el reembolso";

export type RefundBankAccount =
{
    id: number;
    value: string;
    default: boolean;
};

export const mapAuthorizedRefundAccounts = (response: any[] | null | undefined): RefundBankAccount[] =>
{
    if(!Array.isArray(response))
    {
        return [];
    }

    const mapped: RefundBankAccount[] = [];
    let hasDefault = false;
    for(let i = 0; i < response.length; i++)
    {
        const item = response[i];
        const id = Number(item?.id);
        const value = typeof item?.accountOrigin === "string" ? item.accountOrigin : "";
        if(!Number.isFinite(id) || value === "")
        {
            continue;
        }

        // Un reembolso solo admite una cuenta destino. Si el API marca varias
        // isSelected, nos quedamos con la primera.
        const isDefault = item?.isSelected === true && !hasDefault;
        if(isDefault)
        {
            hasDefault = true;
        }
        mapped.push({id, value, default: isDefault});
    }

    return mapped;
};

/**
 * Las cuentas autorizadas mandan. Si el IBAN restaurado (vuelta de firma)
 * sigue en la lista, se conserva; si no, la cuenta isSelected; si no hay
 * ninguna marcada, no se inventa una selección.
 */
export const resolveRefundIban = (accounts: RefundBankAccount[], restoredIban?: string): string =>
{
    if(typeof restoredIban === "string" && restoredIban !== "")
    {
        const restored = accounts.find((account) => account.value === restoredIban);
        if(restored != null)
        {
            return restored.value;
        }
    }

    const defaultAccount = accounts.find((account) => account.default);
    if(defaultAccount != null)
    {
        return defaultAccount.value;
    }

    return "";
};

export const getRefundIbanSelectionError = (accounts: RefundBankAccount[], selectedIban: string): string =>
{
    if(accounts.length === 0)
    {
        return RefundIbanRequiredMessage;
    }
    if(selectedIban === "")
    {
        return RefundIbanRequiredMessage;
    }
    const match = accounts.find((account) => account.value === selectedIban);
    if(match == null)
    {
        return RefundIbanRequiredMessage;
    }
    return "";
};

export const hasCompleteRefundEstimationDates = (dates: {ventaValor?: string; ventaLiquidacion?: string; finalProceso?: string} | null | undefined): boolean =>
{
    if(dates == null)
    {
        return false;
    }

    return typeof dates.ventaValor === "string" && dates.ventaValor.trim() !== ""
        && typeof dates.ventaLiquidacion === "string" && dates.ventaLiquidacion.trim() !== ""
        && typeof dates.finalProceso === "string" && dates.finalProceso.trim() !== "";
};

export const ProcessErrorContactMessage = "Se ha producido un error al procesar el Reembolso. Por favor, si persiste el fallo, contacta con info@ironia.tech";
const DefaultCreateOrderErrorMessage = "Error al crear la orden. Inténtelo de nuevo.";
const DefaultInvalidRefundCodeMessage = "Clave de reembolso inválida.";

export type RefundCreateOrderErrorField = "refundKey" | "iban" | "sourceFund" | "order";

export type RefundCreateOrderCatchResult =
{
    kind: "upgrade";
} |
{
    kind: "invalidRefundCode";
    message: string;
} |
{
    kind: "server";
} |
{
    kind: "field";
    field: RefundCreateOrderErrorField;
    message: string;
} |
{
    kind: "generic";
    message: string;
};

export const isRefundCreateOrderErrorField = (value: unknown): value is RefundCreateOrderErrorField =>
{
    return value === "refundKey" || value === "iban" || value === "sourceFund" || value === "order";
};

/** Ramifica el catch de createOrder por status HTTP y code/field del contrato. El 403 se reconoce por status, no por el body. */
export const resolveCreateOrderCatch = (status: unknown, data: unknown): RefundCreateOrderCatchResult =>
{
    const body = data != null && typeof data === "object" ? data as {code?: unknown; message?: unknown; field?: unknown} : undefined;
    const code = body != null && typeof body.code === "string" ? body.code : undefined;
    const rawMessage = body != null && typeof body.message === "string" ? body.message : undefined;
    const message = rawMessage != null && rawMessage.trim() !== "" ? rawMessage : undefined;
    const field = body != null && isRefundCreateOrderErrorField(body.field) ? body.field : "order";
    const httpStatus = typeof status === "number" ? status : undefined;

    if(httpStatus === 409)
    {
        return {kind: "upgrade"};
    }
    if(httpStatus === 403 || code === "INVALID_REFUND_CODE")
    {
        return {kind: "invalidRefundCode", message: message ?? DefaultInvalidRefundCodeMessage};
    }
    if(httpStatus != null && httpStatus >= 500)
    {
        return {kind: "server"};
    }
    if(httpStatus === 400 || httpStatus === 404)
    {
        return {kind: "field", field, message: message ?? DefaultCreateOrderErrorMessage};
    }
    return {kind: "generic", message: message ?? DefaultCreateOrderErrorMessage};
};